// @ts-ignore - This import is needed for Supabase migration
import { supabase } from '@/lib/supabase';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';

// No-op functions for removed debug logging
const debugLog = (..._: any[]) => {};
const debugError = (..._: any[]) => {};
const debugWarn = (..._: any[]) => {};



import {
  LayoutDashboard,
  Receipt,
  PieChart,
  MessageCircleQuestion,
  Settings, 
  LogOut,
  CreditCard,
  Wallet,
  Building2,
  ChevronRight,
  ChevronDown,
  Plus,
  ArrowRight,
  ArrowLeft,
  User,
  Sun,
  Moon,
  Calculator,
  FolderIcon,
  Pencil,
  ListFilter,
  Settings2,
  Users,
  Tags,
  FileText,
  Repeat,
  Bell,
  Globe,
  Wallet2,
  FolderCog,
  Target,
  Heart
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useAccounts } from '@/store/accountsStore';
import { useTransactionStore } from '@/store/transactionsStore';
import { useProfileStore } from '@/store/profileStore';
import { useCurrenciesStore } from '@/store/currenciesStore';
import { Currency as StoreCurrency } from '@/store/currenciesStore';
import { useCategoriesStore } from '@/store/categoriesStore';
import { usePayeesStore } from '@/store/payeesStore';
import { useTagsStore } from '@/store/tagsStore';
import { useFoldersStore } from '@/store/foldersStore';
import { useSmartListsStore } from '@/store/smartListsStore';
import { useTheme } from '../ThemeProvider';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuPortal } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import AddTransaction from '@/components/transactions/AddTransaction';
import { AddFolderModal } from '@/components/folders/AddFolderModal';
import { useSettingsStore } from '@/store/settingsStore';
import { NotificationsDropdown } from '@/components/layout/NotificationsDropdown';
import { EditFolderModal } from '@/components/folders/EditFolderModal';
import { EnhancedSmartListModal } from '@/components/smart-lists/EnhancedSmartListModal';
import { EditSmartListModal } from '@/components/smart-lists/EditSmartListModal';
import { SmartList } from '@/types/smartList';
import { Folder } from '@/types/folder';
import { TransactionFilters, TransactionType } from '@/types/transaction';
import { useCurrencyConversion } from '@/hooks/useCurrencyConversion';
import { useTranslation } from 'react-i18next';
import { useOnboardingStore } from '@/store/onboardingStore';
import { DateRange } from '@/types/base';
import LogoDark from '../../assets/logo-dark.svg?url'

// Define CoreAccount type locally to avoid import issues
interface CoreAccount {
  id: string;
  name: string;
  type: string;
  balance: number;
  currency: string;
  color?: string;
  icon?: string;
  isDefault?: boolean;
  isArchived?: boolean;
  userId?: string;
}

import { useAccountsStore as useCoreAccountsStore } from '@flows.cash/core';
import { useCurrenciesStore as useCoreCurrenciesStore, useCategoriesStore as useCoreCategories, useTransactionStore as useCoreTransactionStore } from '@flows.cash/core';
import { useProfilePhoto } from '@/hooks/useProfilePhoto'

// Mock functions for migration
const waitForAuthReady = async (): Promise<boolean> => true;
const forceTokenRefresh = async (): Promise<boolean> => true;

interface ExtendedUser {
  id: string;
  email?: string;
}

interface ExtendedAccount extends Omit<CoreAccount, 'currency'> {
  currency?: StoreCurrency;
  calculatedBalance: number; // Server-calculated balance from account_balances_view
}

interface GroupedAccounts {
  [key: string]: ExtendedAccount[];
}

interface AccountTypeInfo {
  name: string;
  icon: React.ElementType;
}

interface TransactionsStore {
  transactions: {
    filters: TransactionFilters;
  };
  setFilters: (filters: TransactionFilters) => void;
  fetchTransactions: (userId: string) => Promise<void>;
  // Note: Removed getAccountBalance - all balances now come from account_balances_view
}

type ExtendedFolder = Folder & {
  account_ids: string[];
  userId: string;
  createdAt: any;
  updatedAt: any;
};

type ExtendedSmartList = SmartList & {
  createdAt: any;
  updatedAt: any;
};



// Add this function to get correct currency symbols
const getCurrencySymbol = (currencyCode: string | undefined): string => {
  if (!currencyCode) return '$';
  
  // Define currency symbols
  const symbols: Record<string, string> = {
    'USD': '$',
    'EUR': '€',
    'TRY': '₺', // Turkish Lira
    'GBP': '£',
    'JPY': '¥',
    'CNY': '¥',
    'INR': '₹',
    'CAD': 'C$',
    'AUD': 'A$',
    'CHF': 'Fr',
    'RUB': '₽',
    'BRL': 'R$',
    'ZAR': 'R',
    'KRW': '₩'
  };
  
  return symbols[currencyCode] || currencyCode;
};

// Simple function to format amounts with the correct currency symbol
const formatWithSymbol = (amount: number, currencyCode?: string): string => {
  const symbol = getCurrencySymbol(currencyCode);
  const sign = amount < 0 ? '-' : '';
  const absValue = Math.abs(amount);
  
  // Format with 2 decimal places
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return `${sign}${symbol} ${formatter.format(absValue)}`;
};

// Define AccountType and AccountTypes interfaces  
type AccountType = 'checking' | 'savings' | 'credit' | 'investment' | 'loan' | 'cash' | 'other';

interface AccountTypes {
  [key: string]: AccountTypeInfo;
}

export default function DashboardLayout() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore() as { user: ExtendedUser | null };
  const { accounts: rawAccounts, error: accountsError, loading: accountsLoading } = useAccounts();
  const accounts = rawAccounts as CoreAccount[];
  
  const coreAccountsStore = useCoreAccountsStore();
  const transactionsStore = useTransactionStore() as unknown as TransactionsStore;
  const { transactions, setFilters, fetchTransactions } = transactionsStore;
  const { settings, fetchSettings } = useProfileStore() as {
    settings: any;
    fetchSettings: (userId: string) => Promise<void>;
  };
  const { currencies, fetchCurrencies } = useCurrenciesStore();
  const { fetchCategories } = useCategoriesStore();
  const { fetchPayees } = usePayeesStore();
  const { fetchTags } = useTagsStore();
  const { theme, setTheme } = useTheme();
  const { fetchSettings: fetchUserSettings } = useSettingsStore();
  const { folders, fetchFolders } = useFoldersStore() as unknown as { folders: ExtendedFolder[], fetchFolders: (userId: string) => Promise<void> };
  const { smartLists, fetchSmartLists } = useSmartListsStore() as unknown as { smartLists: ExtendedSmartList[], fetchSmartLists: (userId: string) => Promise<void> };
  const { checkUserProfile } = useOnboardingStore();
  const { convertToDefault, defaultCurrency } = useCurrencyConversion();
  
  const [showAccounts, setShowAccounts] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [showAddFolder, setShowAddFolder] = useState(false);
  const [showAddSmartList, setShowAddSmartList] = useState(false)
  const [showEditSmartListModal, setShowEditSmartListModal] = useState(false)
  const [editingFolder, setEditingFolder] = useState<ExtendedFolder | null>(null);
  const [editingSmartList, setEditingSmartList] = useState<ExtendedSmartList | null>(null)
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())
  const [activeSmartListId, setActiveSmartListId] = useState<string | null>(null)
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = useState(false);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [accountsLoadRetries, setAccountsLoadRetries] = useState(0);
  const accountsLoadAttemptedRef = useRef<{[key: string]: boolean}>({});
  const maxAccountsLoadRetries = 3;
  const coreTransactionStore = useCoreTransactionStore();


  // State to track dismissed notifications
  const [dismissedNotifications, setDismissedNotifications] = useState<{
    accountsLoading: boolean;
    accountsError: boolean;
    noAccounts: boolean;
  }>({
    accountsLoading: false,
    accountsError: false,
    noAccounts: false
  });

  // Move settingsNavigation here, after t is defined
  const settingsNavigation = [
    { 
      name: "General", 
      href: '/app/settings', 
      icon: Settings2,
      exact: true
    },
    { 
      name: "Categories", 
      href: '/app/settings/categories', 
      icon: PieChart 
    },
    { 
      name: "Payees", 
      href: '/app/settings/payees', 
      icon: Users 
    },
    { 
      name: "Tags", 
      href: '/app/settings/tags', 
      icon: Tags 
    },
    { 
      name: "Rules", 
      href: '/app/settings/rules', 
      icon: FileText 
    },
    { 
      name: "Recurring Transactions", 
      href: '/app/settings/recurring', 
      icon: Repeat 
    },
    { 
      name: "Notifications", 
      href: '/app/settings/notifications', 
      icon: Bell 
    },
    { 
      name: "Currencies", 
      href: '/app/settings/currency', 
      icon: Wallet2 
    },
    { 
      name: "Language", 
      href: '/app/settings/language', 
      icon: Globe 
    },
    { 
      name: "Data Management", 
      href: '/app/settings/data', 
      icon: FolderCog 
    }
  ];

  // Move accountTypes here, after t is defined
  const accountTypes: AccountTypes = {
    checking: {
      name: t('sidebar.bankAccounts'),
      icon: Building2
    },
    savings: {
      name: t('sidebar.savingsAccounts'),
      icon: Wallet
    },
    credit: {
      name: t('sidebar.creditCards'),
      icon: CreditCard
    },
    investment: {
      name: t('sidebar.investmentAccounts'),
      icon: Target
    },
    loan: {
      name: t('sidebar.loanAccounts'),
      icon: Building2
    },
    cash: {
      name: t('sidebar.cashAccounts'),
      icon: Wallet2
    },
    other: {
      name: t('sidebar.otherAccounts'),
      icon: Building2
    }
  };

  // Ensure accounts and currencies are arrays before using reduce
  // Note: calculatedBalance is kept for backward compatibility but actual balance display uses server-side data
  const groupedAccounts: GroupedAccounts = Array.isArray(accounts) 
    ? accounts.reduce((acc: GroupedAccounts, account: CoreAccount) => {
        if (!acc[account.type]) {
          acc[account.type] = [];
        }
        const currency = Array.isArray(currencies) 
          ? currencies.find(c => c.id === account.currency)
          : undefined;
        acc[account.type].push({
          ...account,
          currency,
          calculatedBalance: account.balance || 0 // Use server-calculated balance from account_balances_view
        });
        return acc;
      }, {} as GroupedAccounts)
    : {} as GroupedAccounts;

  const getTypeTotal = (type: string): number => {
    return (groupedAccounts[type] || [])
      .reduce((total, account) => {
        // Use the account's balance field directly (like Accounts.tsx does)
        const balance = account.balance || 0;
        
        // Get currency code from account data
        const currencyId = typeof account.currency === 'string' ? account.currency : undefined;
        const currency = currencyId ? currencies.find(c => c.id === currencyId) : undefined;
        const currencyCode = currency?.code || defaultCurrency;
        
        return total + convertToDefault(balance, currencyCode);
      }, 0);
  };

  // Enhanced accounts loading function with retry mechanism
  const loadAccounts = useCallback(async (userId: string): Promise<CoreAccount[]> => {
    debugLog('🔄 Attempting to load accounts for user:', userId);
    
    try {
      // Check Supabase connection first
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        debugError('❌ Supabase session error:', sessionError);
        throw new Error(`Supabase session error: ${sessionError.message}`);
      }
      
      if (!sessionData.session) {
        debugWarn('⚠️ No active Supabase session found');
        // Force token refresh
        await forceTokenRefresh();
      }
      
      // Use the core accounts store to fetch accounts
      const loadedAccounts = await coreAccountsStore.fetchAccounts(userId);
      debugLog(`✅ Accounts loaded successfully: ${loadedAccounts.length} accounts`);
      
      // Reset retry counter on success
      setAccountsLoadRetries(0);
      
      return loadedAccounts;
    } catch (error) {
      debugError('❌ Failed to load accounts:', error);
      
      // Increment retry counter
      const newRetryCount = accountsLoadRetries + 1;
      setAccountsLoadRetries(newRetryCount);
      
      if (newRetryCount < maxAccountsLoadRetries) {
        debugLog(`🔄 Retrying accounts load (attempt ${newRetryCount + 1}/${maxAccountsLoadRetries})...`);
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, newRetryCount)));
        return loadAccounts(userId);
      } else {
        debugError(`❌ Failed to load accounts after ${maxAccountsLoadRetries} attempts`);
        return [];
      }
    }
  }, [accountsLoadRetries, coreAccountsStore]);

  // Centralized transaction refresh function
  const refreshTransactions = useCallback(async () => {
    if (!user?.id) return;
    
    debugLog('🔄 Refreshing transactions from DashboardLayout');
    try {
      // Use the core transaction store to fetch transactions
      await coreTransactionStore.fetchTransactions(user.id);
      
      // Also update the local store for backward compatibility
      if (typeof transactionsStore.fetchTransactions === 'function') {
        await transactionsStore.fetchTransactions(user.id);
      }
    } catch (error) {
      debugError('❌ Error refreshing transactions:', error);
    }
  }, [user?.id, coreTransactionStore, transactionsStore]);



  // Load initial data when component mounts
  useEffect(() => {
    if (isInitialDataLoaded || !user?.id) {
      if (!user?.id) {
        debugLog('❌ No user ID available, skipping data loading');
      } else {
        debugLog('✅ Initial data already loaded, skipping redundant load');
      }
      return;
    }

    // Check if we've already attempted to load accounts for this user
    if (accountsLoadAttemptedRef.current[user.id]) {
      debugLog('🔄 Already attempted to load accounts for this user, skipping redundant load');
      return;
    }

    // Mark that we've attempted to load accounts for this user
    accountsLoadAttemptedRef.current[user.id] = true;

    // Use an immediately invoked async function to handle async operations
    (async () => {
      try {
        debugLog('🔄 Loading initial data for user:', user.id);
        
        // Wait for auth to be ready before proceeding
        const isAuthenticated = await waitForAuthReady();
        if (!isAuthenticated) {
          debugLog('❌ User not authenticated, skipping data loading');
          return;
        }
        
        // Force refresh the auth token to ensure we have the latest permissions
        await forceTokenRefresh();
        
        // Load data in parallel with individual error handling
        const loadPromises = [
          // Step 1: Load accounts (with enhanced retry mechanism)
          (async () => {
            debugLog('🔍 Step 1: Loading accounts...');
            try {
              const loadedAccounts = await loadAccounts(user.id);
              return { type: 'accounts', success: true, data: loadedAccounts };
            } catch (error) {
              debugError('❌ Failed to load accounts:', error);
              return { type: 'accounts', success: false, error };
            }
          })(),
          
          // Step 2: Load settings
          (async () => {
            debugLog('🔍 Step 2: Loading settings...');
            try {
              await fetchSettings(user.id);
              debugLog('✅ Settings loaded successfully');
              return { type: 'settings', success: true };
            } catch (error) {
              debugError('❌ Failed to load settings:', error);
              return { type: 'settings', success: false, error };
            }
          })(),
          
          // Step 3: Load transactions
          (async () => {
            debugLog('🔍 Step 3: Loading transactions...');
            try {
              await refreshTransactions();
              debugLog('✅ Transactions loaded successfully');
              return { type: 'transactions', success: true };
            } catch (error) {
              debugError('❌ Failed to load transactions:', error);
              return { type: 'transactions', success: false, error };
            }
          })(),
          
          // Step 4: Load currencies
          (async () => {
            debugLog('🔍 Step 4: Loading currencies...');
            try {
              await fetchCurrencies();
              debugLog('✅ Currencies loaded successfully');
              return { type: 'currencies', success: true };
            } catch (error) {
              debugError('❌ Failed to load currencies:', error);
              return { type: 'currencies', success: false, error };
            }
          })(),
          
          // Step 5: Load categories
          (async () => {
            debugLog('🔍 Step 5: Loading categories...');
            try {
              await fetchCategories(user.id);
              debugLog('✅ Categories loaded successfully');
              return { type: 'categories', success: true };
            } catch (error) {
              debugError('❌ Failed to load categories:', error);
              return { type: 'categories', success: false, error };
            }
          })(),
          
          // Step 6: Load remaining data
          (async () => {
            debugLog('🔍 Step 6: Loading remaining data...');
            try {
              await Promise.all([
                fetchPayees(user.id),
                fetchTags(user.id),
                fetchUserSettings(user.id),
                fetchFolders(user.id),
                fetchSmartLists(user.id),
                checkUserProfile(user.id),
              ]);
              debugLog('✅ Remaining data loaded successfully');
              return { type: 'remaining', success: true };
            } catch (error) {
              debugError('❌ Failed to load some remaining data:', error);
              return { type: 'remaining', success: false, error };
            }
          })(),
          
          // Note: Account balances are now loaded directly from account.balance field (updated by core store)
        ];
        
        // Wait for all promises to resolve
        const results = await Promise.all(loadPromises);
        
        debugLog('✅ Initial data loading completed');
        debugLog('📊 Data Loading Results:');
        results.forEach(result => {
          debugLog(`- ${result.type}: ${result.success ? 'Success' : 'Failed'}`);
        });
        
        // Detect if we're in offline mode from Firebase errors
        const isOfflineMode = !!accountsError && (!Array.isArray(accounts) || accounts.length === 0);
        
        if (isOfflineMode) {
          debugWarn('⚠️ Application appears to be in offline mode. Some data may be unavailable or stale.');
        }
        
        // Use setTimeout to ensure all state updates have been processed
        setTimeout(() => {
          // Get the latest state directly from the store
          const currentAccounts = useCoreAccountsStore.getState().accounts || [];
          const coreCurrencies = useCoreCurrenciesStore.getState().currencies || [];
          const coreExchangeRates = useCoreCurrenciesStore.getState().exchangeRates || {};
          
          // Get categories from the core store
          const currentCategories = useCoreCategories.getState().categories || [];
          
          const currentTransactions = useCoreTransactionStore.getState().transactions || [];
          
          debugLog('📊 Data Loading Diagnostic:');
          debugLog(`- Accounts: ${currentAccounts.length}`);
          debugLog(`- Currencies: ${coreCurrencies.length}`);
          debugLog(`- Transactions: ${currentTransactions.length}`);
          debugLog(`- Categories: ${Array.isArray(currentCategories) ? currentCategories.length : 0}`);
          debugLog(`- Exchange Rates: ${Object.keys(coreExchangeRates || {}).length} currencies`);
          
          // Debug currencies
          debugLog('Core Currencies Detail:', coreCurrencies);
          debugLog('Regular currencies variable value:', currencies);
          
          // Only show warnings if we're not in offline mode
          if (currentAccounts.length === 0 && !isOfflineMode) {
            debugWarn('⚠️ No accounts were loaded. This may cause balance calculation issues.');
            
            // If we have no accounts but we should, try refreshing the token and fetching again
            if (user?.id) {
              debugLog('🔄 Attempting to refresh auth token and fetch accounts again...');
              forceTokenRefresh().then((refreshed: boolean) => {
                if (refreshed) {
                  debugLog('🔄 Token refreshed, fetching accounts again...');
                  loadAccounts(user.id).then((reloadedAccounts: any) => {
                    debugLog(`✅ Accounts reloaded: ${reloadedAccounts.length}`);
                  }).catch((err: any) => {
                    debugError('❌ Failed to reload accounts after token refresh:', err);
                  });
                }
              });
            }
          }
          
          if (coreCurrencies.length === 0 && !isOfflineMode) {
            debugWarn('⚠️ No currencies were loaded. This may cause currency conversion issues.');
          }
          
          if (!coreExchangeRates || Object.keys(coreExchangeRates).length === 0 && !isOfflineMode) {
            debugWarn('⚠️ No exchange rates were loaded. This may cause currency conversion issues.');
          }
        }, 1000);

        setIsInitialDataLoaded(true);
      } catch (error) {
        debugError('❌ Error loading initial data:', error);
      }
    })();
  }, [user?.id, coreTransactionStore, transactionsStore]);



  // Reset data loading state when user changes
  useEffect(() => {
    if (user?.id) {
      // Keep the loaded state if user is the same
    } else {
      setIsInitialDataLoaded(false);
      setAccountsLoadRetries(0);
      accountsLoadAttemptedRef.current = {};
    }
  }, [user?.id]);

  useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const accountId = params.get('accountId');
    
    if (location.pathname === '/app/transactions' && accountId) {
      const filters: TransactionFilters = {
        dateRange: undefined,
        type: undefined,
        categories: [],
        accounts: [accountId],
        search: '',
        tags: []
      };
      setFilters(filters);
      if (user?.id) {
        fetchTransactions(user.id);
      }
      setActiveSmartListId(null);
    }
  }, [location.pathname, location.search, user?.id]);

  // Debug translations
  useEffect(() => {
    debugLog('Current language:', i18n.language);
    debugLog('Translation availability:', {
      'navigation.overview': t('navigation.overview'),
      'sidebar.folders': t('sidebar.folders'),
      'sidebar.accounts': t('sidebar.accounts'),
      'dashboard.navigation.overview': t('dashboard.navigation.overview'),
      'dashboard.navigation.transactions': t('dashboard.navigation.transactions')
    });
  }, [i18n.language, t]);

  const navigation = [
    { name: t('dashboard.navigation.overview'), href: '/app/dashboard', icon: LayoutDashboard },
    { 
      name: t('dashboard.navigation.transactions'), 
      href: '/app/transactions', 
      icon: Receipt,
      onClick: () => {
        setFilters({
          dateRange: undefined,
          type: undefined,
          categories: [],
          accounts: [],
          search: '',
          tags: []
        });
        setActiveSmartListId(null);
      }
    },
    { name: t('dashboard.navigation.analytics'), href: '/app/analytics', icon: PieChart },
    { name: t('dashboard.navigation.moneyChat', 'Ask your money'), href: '/app/money-chat', icon: MessageCircleQuestion },
    { name: t('dashboard.navigation.manageAccounts'), href: '/app/accounts', icon: CreditCard },
    { name: t('dashboard.navigation.budgetManagement'), href: '/app/budget', icon: Calculator },
    {
      name: t('dashboard.navigation.savingGoals'),
      href: '/app/saving-goals',
      icon: Target
    },
    { name: t('notifications.title'), href: '/app/notifications', icon: Bell }
  ];
  
  // Debug log for navigation values
  debugLog('Navigation values:', {
    currentLanguage: i18n.language,
    sidebarFolders: t('sidebar.folders'),
    sidebarAccounts: t('sidebar.accounts'),
    overview: t('dashboard.navigation.overview'),
    transactions: t('dashboard.navigation.transactions')
  });

  const handleSignOut = async () => {
    try {
      // Use the auth store's logout method instead of directly calling supabase
      const authStore = useAuthStore.getState();
      await authStore.logout();
      
      // Clear any remaining auth states from session storage
      sessionStorage.removeItem('auth_completed');
      sessionStorage.removeItem('auth_attempt_count');
      sessionStorage.removeItem('auth_in_progress');
      
      // Force a complete page reload to ensure all state is reset
      window.location.href = '/auth/login';
    } catch (error) {
      debugError('Error signing out:', error);
      // Even if there's an error, try to redirect to login
      window.location.href = '/auth/login';
    }
  };

  const handleAccountClick = (accountId: string) => {
    setActiveSmartListId(null);
    
    // Get current filters from the store to preserve existing settings
    const currentFilters = transactions?.filters || {};
    
    // Create new filters object, preserving existing filters while setting the account
    const filters = {
      ...currentFilters,
      // Set accounts to the clicked account, ensuring it's an array
      accounts: [accountId]
    };
    
    // Set the filters in the store
    setFilters(filters);
    
    // Navigate to transactions page with the accountId parameter
    navigate(`/app/transactions?accountId=${accountId}`);
  };

  const toggleFolder = (folderId: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const renderSmartList = (smartList: ExtendedSmartList) => {
    const IconComponent = smartList.icon ? (LucideIcons as any)[smartList.icon] : ListFilter;
    const isActive = activeSmartListId === smartList.id;
    const listColor = smartList.color || '#6366F1';
    
    return (
      <div key={smartList.id} className="relative group">
        <button
          onClick={() => handleSmartListClick(smartList)}
          className={`w-full flex items-center px-4 py-2 rounded-lg transition-all duration-200`}
          style={{
            backgroundColor: isActive ? listColor : 'transparent',
            color: isActive ? 'white' : 'rgb(229 231 235)',
            ['--list-color' as string]: listColor,
            ['--list-hover-color' as string]: `${listColor}80`
          }}
          title={ensureString(smartList.name)}
          onMouseEnter={(e) => {
            if (!isActive) {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = `${listColor}40`
            }
          }}
          onMouseLeave={(e) => {
            if (!isActive) {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
            }
          }}
        >
          <IconComponent
            className={cn(
              "w-4 h-4",
              !isDesktopSidebarCollapsed && (i18n.language === 'ar' ? "ml-3" : "mr-3")
            )}
            style={{ color: isActive ? 'white' : listColor }}
          />
          {!isDesktopSidebarCollapsed && (
            <span className="text-sm font-medium truncate flex-grow text-left">
              {ensureString(smartList.name)}
            </span>
          )}
        </button>
        {!isDesktopSidebarCollapsed && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              setEditingSmartList(smartList)
              setShowEditSmartListModal(true)
            }}
            className={`absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 rounded transition-all ${
              isActive ? 'text-white hover:bg-black/20' : 'text-gray-400 hover:text-white hover:bg-white/10'
            }`}
          >
            <Settings className="w-3 h-3" />
          </button>
        )}
      </div>
    )
  }

  const handleCloseEditModal = () => {
    setShowEditSmartListModal(false)
    setEditingSmartList(null)
  }

  useEffect(() => {
    if (!transactions?.filters) return;

    const currentFilters = { ...transactions.filters };
    delete currentFilters.accounts;

    const matchingList = smartLists.find(list => {
      const listFilters = { ...list.filters };
      delete (listFilters as any).accounts;
      return JSON.stringify(listFilters) === JSON.stringify(currentFilters);
    });
    
    setActiveSmartListId(matchingList?.id || null);
  }, [transactions?.filters, smartLists]);

  const handleThemeChange = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    debugLog('Changing theme from', theme, 'to', newTheme);
    setTheme(newTheme);
  };

  const handleSmartListClick = (smartList: ExtendedSmartList) => {
    setActiveSmartListId(smartList.id);
    const defaultFilters: TransactionFilters = {
      dateRange: undefined,
      type: undefined,
      categories: [],
      accounts: [],
      search: '',
      tags: []
    };
    setFilters(defaultFilters);
    const smartListFilters: TransactionFilters = {
      ...smartList.filters,
      accounts: [],
      dateRange: smartList.filters.dateRange as DateRange,
      type: smartList.filters.type as TransactionType | undefined
    };
    setFilters(smartListFilters);
    navigate('/app/transactions');
  };

  // Add a helper function to ensure translation values are strings
  const ensureString = (value: any, fallback: string = ''): string => {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object') {
      // If it's an object with title/description/variant (common toast format)
      if ('title' in value) return String(value.title);
      if ('description' in value) return String(value.description);
      // Try to convert object to string in a meaningful way
      return JSON.stringify(value);
    }
    return fallback;
  };

  // Add a debug banner for accounts loading issues
  const renderAccountsDebugBanner = () => {
    if (process.env.NODE_ENV === 'production') return null;
    
    if (accountsLoading && !dismissedNotifications.accountsLoading) {
      return (
        <div className="fixed bottom-4 right-4 z-50 max-w-md bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl shadow-sm p-3 flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-secondary dark:border-primary-dark mr-3"></div>
          <p className="text-sm text-text-primary dark:text-text-dark-primary">
            {ensureString(t('notifications.accounts.loading', 'Accounts are currently loading...'))}
          </p>
          <button 
            onClick={() => setDismissedNotifications(prev => ({ ...prev, accountsLoading: true }))}
            className="ml-3 text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary"
            aria-label={ensureString(t("common.close"), "Close")}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      );
    }
    
    if (accountsError && !dismissedNotifications.accountsError) {
      return (
        <div className="fixed bottom-4 right-4 z-50 max-w-md bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border-l-4 border-l-negative dark:border-l-negative-dark border border-border dark:border-border-dark rounded-xl shadow-sm p-3">
          <div className="flex justify-between items-start">
            <p className="text-sm text-text-primary dark:text-text-dark-primary font-medium">
              {ensureString(t('notifications.accounts.error', 'Error loading accounts'))}
            </p>
            <button 
              onClick={() => setDismissedNotifications(prev => ({ ...prev, accountsError: true }))}
              className="ml-3 text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary"
              aria-label={ensureString(t("common.close"), "Close")}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <p className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1 mb-2 truncate">
            {accountsError.toString()}
          </p>
          <button 
            onClick={() => {
              user?.id && loadAccounts(user.id);
              setDismissedNotifications(prev => ({ ...prev, accountsError: false }));
            }} 
            className="mt-2 px-3 py-1.5 bg-negative text-white hover:bg-negative/90 dark:bg-negative-dark dark:hover:bg-negative-dark/90 rounded-lg text-xs"
          >
            {ensureString(t('common.retry', 'Retry Loading Accounts'))}
          </button>
        </div>
      );
    }
    
    if ((!Array.isArray(accounts) || accounts.length === 0) && !dismissedNotifications.noAccounts) {
      return (
        <div className="fixed bottom-4 right-4 z-50 max-w-md bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border-l-4 border-l-[#FF9800] dark:border-l-[#FF9800] border border-border dark:border-border-dark rounded-xl shadow-sm p-3">
          <div className="flex justify-between items-start">
            <p className="text-sm text-text-primary dark:text-text-dark-primary font-medium">
              {ensureString(t('notifications.accounts.empty', 'No accounts loaded. Some features may not work correctly.'))}
            </p>
            <button 
              onClick={() => setDismissedNotifications(prev => ({ ...prev, noAccounts: true }))}
              className="ml-3 text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary"
              aria-label={ensureString(t("common.close"), "Close")}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <button 
            onClick={() => {
              user?.id && loadAccounts(user.id);
              setDismissedNotifications(prev => ({ ...prev, noAccounts: false }));
            }} 
            className="mt-2 px-3 py-1.5 bg-secondary text-white hover:bg-secondary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 rounded-lg text-xs"
          >
            {ensureString(t('common.load', 'Load Accounts'))}
          </button>
        </div>
      );
    }
    
    return null;
  };

  return (
    <>
      <div 
        dir={i18n.language === 'ar' ? 'rtl' : 'ltr'} 
        className={cn(
          "flex h-screen bg-gradient-to-b from-background-primary to-background-primary/90 dark:from-background-dark-primary dark:to-background-dark-primary/90",
          i18n.language === 'ar' ? 'flex-row-reverse font-arabic' : 'flex-row font-sans',
          "selection:bg-accent-light selection:text-accent"
        )}
        style={{
          fontFamily: i18n.language === 'ar' ? '"Noto Kufi Arabic", system-ui, sans-serif' : 'inherit'
        }}
      >
        {/* Debug banner for development */}
        {process.env.NODE_ENV !== 'production' && (
          <div className="fixed top-16 left-0 right-0 z-50 px-4 py-2">
            {renderAccountsDebugBanner()}
          </div>
        )}
        
        {isMobileSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}

        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <div className={cn(
            "fixed top-0 z-40 transition-all duration-200",
            i18n.language === 'ar' 
              ? "right-0 left-[288px] md:right-[288px] md:left-0" 
              : "right-0 left-0 md:left-[288px]"
          )}>
            <div className="relative">
              <div className="absolute inset-0 bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border-b border-border dark:border-border-dark" />
              <div className="relative px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-end">
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleThemeChange}
                    className="inline-flex items-center justify-center rounded-lg p-2
                      text-text-secondary dark:text-text-dark-secondary 
                      hover:text-text-primary dark:hover:text-text-dark-primary
                      hover:bg-background-hover dark:hover:bg-background-dark-hover
                      focus:outline-none focus:ring-2 focus:ring-accent
                      backdrop-blur-sm
                      transition-all duration-200"
                    title={theme === 'dark' ? t('dashboard.theme.light') : t('dashboard.theme.dark')}
                  >
                    {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                  </button>

                  <div className="relative">
                    <NotificationsDropdown />
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="flex items-center gap-2 rounded-lg 
                          bg-background-input dark:bg-background-dark-input
                          hover:bg-background-hover dark:hover:bg-background-dark-hover
                          backdrop-blur-sm
                          py-1.5 pl-2 pr-3 sm:pr-4 
                          text-sm font-medium text-text-primary dark:text-text-dark-primary
                          focus:outline-none focus:ring-2 focus:ring-accent
                          transition-all duration-200 
                          shadow-lg"
                      >
                        <Avatar className="h-7 w-7 sm:h-8 sm:w-8 ring-2 ring-accent transition-all duration-200">
                          <AvatarImage 
                            src={useProfilePhoto().data?.url}
                            alt={settings?.full_name || user?.email || 'User profile'}
                            className={cn(
                              "transition-opacity duration-200",
                              useProfilePhoto().isLoading && "opacity-0"
                            )}
                            onError={(e) => {
                              console.error('Error loading avatar image:', e)
                              // Hide broken image icon
                              ;(e.target as HTMLImageElement).style.display = 'none'
                            }}
                          />
                          <AvatarFallback className="bg-gradient-to-br from-accent to-accent-hover text-white">
                            {settings?.full_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="hidden sm:block truncate max-w-[120px] font-medium">
                          {settings?.display_name || settings?.full_name || user?.email}
                        </span>
                        <ChevronDown className="w-4 h-4 text-text-muted dark:text-text-dark-muted" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent 
                      align="end" 
                      className={cn(
                        "w-56 bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark shadow-xl rounded-lg mt-1",
                        i18n.language === 'ar' && "font-arabic"
                      )}
                    >
                      <DropdownMenuLabel className={cn(
                        "text-text-primary dark:text-text-dark-primary font-normal px-3 py-2",
                        i18n.language === 'ar' && "font-arabic"
                      )}>
                        <div className="flex flex-col space-y-1">
                          <p className="text-sm font-medium">{settings?.full_name}</p>
                          <p className="text-xs text-text-muted dark:text-text-dark-muted truncate">{user?.email}</p>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator className="bg-border dark:bg-border-dark" />
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger className={cn(
                          "flex w-full items-center cursor-pointer text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary px-3 py-2 hover:bg-background-hover dark:hover:bg-background-dark-hover transition-colors",
                          i18n.language === 'ar' && "font-arabic"
                        )}>
                          <Settings className={cn("h-4 w-4", i18n.language === 'ar' ? "ml-0 mr-2" : "mr-2")} />
                          <span>{t('settings.title')}</span>
                        </DropdownMenuSubTrigger>
                        <DropdownMenuPortal>
                          <DropdownMenuSubContent 
                            className={cn(
                              "min-w-[12rem] bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark shadow-xl rounded-lg",
                              i18n.language === 'ar' && "font-arabic"
                            )}
                            sideOffset={2}
                          >
                            {settingsNavigation.map((item) => {
                              return (
                                <DropdownMenuItem 
                                  key={item.href} 
                                  onClick={() => navigate(item.href)}
                                  className={cn(
                                    "flex items-center cursor-pointer text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary px-3 py-2 hover:bg-background-hover dark:hover:bg-background-dark-hover transition-colors",
                                    i18n.language === 'ar' && "font-arabic"
                                  )}
                                >
                                  <item.icon className={cn("h-4 w-4", i18n.language === 'ar' ? "ml-0 mr-2" : "mr-2")} />
                                  <span>{item.name}</span>
                                </DropdownMenuItem>
                              );
                            })}
                          </DropdownMenuSubContent>
                        </DropdownMenuPortal>
                      </DropdownMenuSub>
                      <DropdownMenuItem 
                        onClick={() => navigate('/app/settings')}
                        className={cn(
                          "flex items-center cursor-pointer text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary px-3 py-2 hover:bg-background-hover dark:hover:bg-background-dark-hover transition-colors",
                          i18n.language === 'ar' && "font-arabic"
                        )}
                      >
                        <User className={cn("h-4 w-4", i18n.language === 'ar' ? "ml-0 mr-2" : "mr-2")} />
                        <span className="inline-block">{t('dashboard.userMenu.profile')}</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-border dark:bg-border-dark" />
                      <DropdownMenuItem 
                        onClick={handleSignOut}
                        className={cn(
                          "flex items-center cursor-pointer text-error hover:text-error/90 px-3 py-2 hover:bg-error/10 transition-colors",
                          i18n.language === 'ar' && "font-arabic"
                        )}
                      >
                        <LogOut className="w-4 h-4 mr-2 text-error" />
                        <span>{t('dashboard.userMenu.signOut')}</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          </div>

          {showAddTransaction && (
            <AddTransaction
              onClose={() => {
                setShowAddTransaction(false);
                if (user?.id) {
                  fetchTransactions(user.id);
                }
              }}
            />
          )}

          <div className="flex-1 flex overflow-hidden pt-16">
            <div className="flex-1 overflow-y-auto">
              <div className="min-h-[calc(100vh-4rem)] bg-background-primary/40 dark:bg-background-dark-primary/40 backdrop-blur-sm">
                <Outlet context={{ nested: true, refreshTransactions }} />
                <footer className="py-4 text-center text-sm text-text-muted dark:text-text-dark-muted space-y-1">
                  <p className="flex items-center justify-center gap-1">
                    {t('dashboard.footer.madeWith')} <Heart className="w-4 h-4 text-error fill-current" /> {t('dashboard.footer.byDeveloper')}
                  </p>
                  <p>&copy; {new Date().getFullYear()} Flows.Cash. {t('dashboard.footer.copyright')}</p>
                </footer>
              </div>
            </div>
          </div>
        </div>

        <aside className={cn(
          "hidden md:flex flex-col bg-gradient-to-b from-[#081f3d] to-[#111729] relative z-50",
          isDesktopSidebarCollapsed ? "w-20" : "w-72",
          i18n.language === 'ar' 
            ? "border-l border-white/10 font-arabic shadow-[-8px_0_20px_rgba(0,0,0,0.2)]" 
            : "border-r border-white/10 font-sans shadow-[8px_0_20px_rgba(0,0,0,0.2)]",
          i18n.language === 'ar' ? "order-last" : "order-first"
        )}>
          <div className="flex flex-col h-full">
            <div className="flex items-center h-16 px-6 border-b border-white/5 justify-between">
              <div className="flex items-center">
                <img 
                  src={LogoDark}
                  alt="Flows.Cash Logo"
                  className={cn(
                    "h-8",
                    isDesktopSidebarCollapsed ? "w-8" : "w-auto"
                  )}
                />
              </div>
              <button
                onClick={() => setIsDesktopSidebarCollapsed(!isDesktopSidebarCollapsed)}
                className="text-white/80 hover:text-white hover:bg-white/5 p-1 rounded-lg transition-all duration-200"
              >
                {isDesktopSidebarCollapsed 
                  ? (i18n.language === 'ar' ? <ArrowLeft className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />)
                  : (i18n.language === 'ar' ? <ArrowRight className="w-5 h-5" /> : <ArrowLeft className="w-5 h-5" />)
                }
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <nav className="px-4 py-4 space-y-2">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={(e) => {
                        if (item.onClick) {
                          e.preventDefault();
                          item.onClick();
                          navigate(item.href);
                        }
                        setIsMobileSidebarOpen(false);
                      }}
                      className={cn(
                        "flex items-center px-4 py-3 text-sm font-medium rounded-button transition-all duration-200",
                        isDesktopSidebarCollapsed && "justify-center",
                        isActive 
                          ? "bg-[#439476]/20 text-[#439476]"
                          : "text-white/80 hover:bg-[#439476]/10"
                      )}
                      title={isDesktopSidebarCollapsed ? item.name : undefined}
                    >
                      <Icon className={cn(
                        "w-5 h-5",
                        !isDesktopSidebarCollapsed && (i18n.language === 'ar' ? "ml-3" : "mr-3")
                      )} />
                      {!isDesktopSidebarCollapsed && item.name}
                    </Link>
                  );
                })}
              </nav>

              <div className="px-4 py-4 space-y-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between px-2">
                    <span className={cn(
                      "text-sm font-medium text-gray-300",
                      isDesktopSidebarCollapsed && "hidden"
                    )}>{t('dashboard.smartLists.title')}</span>
                    {!isDesktopSidebarCollapsed && (
                      <button
                        onClick={() => setShowAddSmartList(true)}
                        className="p-1 text-white bg-[#439476] hover:bg-[#387565] rounded-lg transition-colors shadow-sm"
                        title="Add Smart List"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {smartLists.map(renderSmartList)}
                </div>

                <div className="space-y-1 pl-2">
                  <div className="flex items-center justify-between px-2">
                    <span className={cn(
                      "text-sm font-medium text-gray-300",
                      isDesktopSidebarCollapsed && "hidden"
                    )}>{t('dashboard.folders.title')}</span>
                    {!isDesktopSidebarCollapsed && (
                      <button
                        onClick={() => setShowAddFolder(true)}
                        className="p-1 text-white bg-[#439476] hover:bg-[#387565] rounded-lg transition-colors shadow-sm"
                        title="Add Folder"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {folders.map(folder => {
                    const folderAccounts = accounts.filter((account: CoreAccount) => 
                      folder.account_ids.includes(account.id)
                    );
                    
                    // Desktop view folder total calculation using server-side balances
                    let desktopFolderTotal = 0;
                    if (folderAccounts.length > 0) {
                      debugLog(`Desktop: Calculating total for folder ${folder.name} with ${folderAccounts.length} accounts`);
                      
                      desktopFolderTotal = folderAccounts.reduce((total: number, account: CoreAccount) => {
                        // Use the account's balance field directly (like Accounts.tsx does)
                        const desktopBalance = account.balance || 0;
                        
                        // Enhanced currency detection (same as individual account display)
                        let currencyCode = defaultCurrency;
                        
                        // First try: check if account.currency is already a currency code string
                        if (typeof account.currency === 'string' && account.currency.length === 3) {
                          currencyCode = account.currency;
                        } else if (typeof account.currency === 'string') {
                          // Second try: lookup by ID in currencies array
                          const currencyObj = currencies.find(c => c.id === account.currency);
                          if (currencyObj && (currencyObj as any).code) {
                            currencyCode = (currencyObj as any).code;
                          }
                        } else if (account.currency && typeof account.currency === 'object' && (account.currency as any).code) {
                          // Third try: currency is already an object with code
                          currencyCode = (account.currency as any).code;
                        }
                        
                        // Convert the balance to the default currency using exchange rates
                        const desktopConvertedBalance = convertToDefault(desktopBalance, currencyCode);
                        
                        return total + desktopConvertedBalance;
                      }, 0);
                      
                      debugLog(`Desktop: Total for folder ${folder.name}: ${desktopFolderTotal} ${defaultCurrency}`);
                    }
                    
                    const isExpanded = expandedFolders.has(folder.id);

                    return (
                      <div key={folder.id} className="space-y-0.5">
                        <div className="flex items-center px-2 py-0.5 text-sm text-gray-400 group space-x-2">
                          <button
                            onClick={() => toggleFolder(folder.id)}
                            className={cn(
                              "flex-1 flex items-center justify-between hover:bg-[#2F2F2F]/5 dark:hover:bg-[#E8E8E8]/5 rounded-lg transition-colors px-2 py-1",
                              isDesktopSidebarCollapsed && "justify-center"
                            )}
                            title={isDesktopSidebarCollapsed ? folder.name : undefined}
                          >
                            <div className="flex items-center min-w-0 flex-1">
                              {!isDesktopSidebarCollapsed && (isExpanded ? (
                                <ChevronDown className={cn("flex-shrink-0 w-4 h-4", i18n.language === 'ar' ? "ml-1" : "mr-1")} />
                              ) : (
                                <ChevronRight className={cn("flex-shrink-0 w-4 h-4", i18n.language === 'ar' ? "ml-1" : "mr-1")} />
                              ))}
                              <FolderIcon className={cn(
                                "flex-shrink-0 w-4 h-4",
                                !isDesktopSidebarCollapsed && (i18n.language === 'ar' ? "ml-2" : "mr-2")
                              )} />
                              {!isDesktopSidebarCollapsed && (
                                <span className="font-medium truncate">{folder.name}</span>
                              )}
                            </div>
                            {!isDesktopSidebarCollapsed && (
                              <span className={cn(
                                "font-medium transition-all whitespace-nowrap flex-shrink-0 min-w-[80px] text-right",
                                desktopFolderTotal === 0 ? "text-white/80 [text-shadow:0_0_10px_rgba(255,255,255,0.3)]" :
                                desktopFolderTotal < 0 ? "text-red-400 [text-shadow:0_0_10px_rgba(248,113,113,0.5)]" :
                                "text-green-400 [text-shadow:0_0_10px_rgba(74,222,128,0.5)]"
                              )}>
                                {formatWithSymbol(desktopFolderTotal, defaultCurrency)}
                              </span>
                            )}
                          </button>
                          {!isDesktopSidebarCollapsed && (
                            <button
                              onClick={() => setEditingFolder(folder)}
                              className="p-1 ml-1 text-[#439476] hover:bg-[#439476]/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                              title="Edit Folder"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        {isExpanded && folderAccounts.map((account: CoreAccount) => {
                          // Use the account's balance field directly (like Accounts.tsx does)
                          // This should already contain the server-side calculated balance
                          const balance = account.balance || 0;
                          

                          

                          
                          const AccountTypeIcon = (accountTypes[account.type] || accountTypes.other).icon;
                          
                          return (
                            <button
                              key={account.id}
                              onClick={() => handleAccountClick(account.id)}
                              className={cn(
                                "w-full text-sm rounded-button transition-all duration-200 px-4 py-2",
                                location.search.includes(account.id)
                                  ? "bg-[#439476]/20 text-[#439476]"
                                  : "text-white/80 hover:bg-[#439476]/10"
                              )}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <AccountTypeIcon className={cn(
                                    "w-4 h-4",
                                    i18n.language === 'ar' ? "ml-2" : "mr-2"
                                  )} />
                                  <span>{account.name}</span>
                                </div>
                                <span className={cn(
                                  "font-medium transition-all whitespace-nowrap",
                                  balance === 0 ? "text-white/80 [text-shadow:0_0_10px_rgba(255,255,255,0.3)]" :
                                  balance < 0 ? "text-red-400 [text-shadow:0_0_10px_rgba(248,113,113,0.5)]" :
                                  "text-green-400 [text-shadow:0_0_10px_rgba(74,222,128,0.5)]"
                                )}>
{(() => {
                                    // Enhanced currency detection
                                    let finalCurrencyCode = defaultCurrency;
                                    
                                    // First try: check if account.currency is already a currency code string
                                    if (typeof account.currency === 'string' && account.currency.length === 3) {
                                      finalCurrencyCode = account.currency;
                                    } else if (typeof account.currency === 'string') {
                                      // Second try: lookup by ID in currencies array
                                      const currencyObj = currencies.find(c => c.id === account.currency);
                                      if (currencyObj && (currencyObj as any).code) {
                                        finalCurrencyCode = (currencyObj as any).code;
                                      }
                                    } else if (account.currency && typeof account.currency === 'object' && (account.currency as any).code) {
                                      // Third try: currency is already an object with code
                                      finalCurrencyCode = (account.currency as any).code;
                                    }
                                    
                                    return formatWithSymbol(balance, finalCurrencyCode);
                                  })()}
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between px-2">
                    <button
                      onClick={() => setShowAccounts(!showAccounts)}
                      className={cn(
                        "flex items-center text-sm font-medium text-[#439476] hover:bg-[#439476]/10 rounded-lg transition-colors p-2",
                        isDesktopSidebarCollapsed && "mx-auto"
                      )}
                      title={isDesktopSidebarCollapsed ? t('sidebar.accounts') : undefined}
                    >
                      {!isDesktopSidebarCollapsed && <span>{t('sidebar.accounts')}</span>}
                      {!isDesktopSidebarCollapsed && (showAccounts ? (
                        <ChevronDown className="w-4 h-4 ml-1" />
                      ) : (
                        <ChevronRight className="w-4 h-4 ml-1" />
                      ))}
                      {isDesktopSidebarCollapsed && <Wallet className="w-4 h-4" />}
                    </button>
                  </div>

                  {showAccounts && !isDesktopSidebarCollapsed && (
                    <div className="space-y-4">
                      {Object.entries(accountTypes).map(([type, { name, icon: Icon }]) => (
                        <div key={type} className="space-y-2">
                          <div className={cn(
                            "flex items-center justify-between text-sm text-gray-400",
                            isDesktopSidebarCollapsed && "justify-center"
                          )}>
                            <div className="flex items-center">
                              <Icon className="w-4 h-4 mr-2" />
                              {!isDesktopSidebarCollapsed && <span className="font-medium">{name}</span>}
                            </div>
                            {!isDesktopSidebarCollapsed && (
                              <span className={cn(
                                "font-medium transition-all whitespace-nowrap",
                                getTypeTotal(type) === 0 ? "text-white/80 [text-shadow:0_0_10px_rgba(255,255,255,0.3)]" :
                                getTypeTotal(type) < 0 ? "text-red-400 [text-shadow:0_0_10px_rgba(248,113,113,0.5)]" :
                                "text-green-400 [text-shadow:0_0_10px_rgba(74,222,128,0.5)]"
                              )}>
                                {formatWithSymbol(getTypeTotal(type), defaultCurrency)}
                              </span>
                            )}
                          </div>
                          {groupedAccounts[type]?.map((account) => {
                            // Use the account's balance field directly (like Accounts.tsx does)
                            // This should already contain the server-side calculated balance
                            const balance = account.balance || 0;
                            

                            

                            
                            const TypeIcon = (accountTypes[account.type] || accountTypes.other).icon;
                            
                            return (
                              <button
                                key={account.id}
                                onClick={() => {
                                  handleAccountClick(account.id);
                                  setIsMobileSidebarOpen(false);
                                }}
                                className={cn(
                                  "w-full text-sm rounded-button transition-all duration-200 px-4 py-2",
                                  location.search.includes(account.id)
                                    ? "bg-[#439476]/20 text-[#439476]"
                                    : "text-white/80 hover:bg-[#439476]/10"
                                )}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <TypeIcon className={cn(
                                      "w-4 h-4",
                                      i18n.language === 'ar' ? "ml-2" : "mr-2"
                                    )} />
                                    <span>{account.name}</span>
                                  </div>
                                  <span className={cn(
                                    "font-medium transition-all whitespace-nowrap",
                                    balance === 0 ? "text-white/80 [text-shadow:0_0_10px_rgba(255,255,255,0.3)]" :
                                    balance < 0 ? "text-red-400 [text-shadow:0_0_10px_rgba(248,113,113,0.5)]" :
                                    "text-green-400 [text-shadow:0_0_10px_rgba(74,222,128,0.5)]"
                                  )}>
{(() => {
                                      // Enhanced currency detection for ExtendedAccount
                                      let finalCurrencyCode = defaultCurrency;
                                      
                                      // Type assertion to handle the mixed types
                                      const currency = account.currency as string | StoreCurrency | undefined;
                                      
                                      // Handle string currency (either ID or code)
                                      if (typeof currency === 'string') {
                                        if (currency.length === 3) {
                                          // Likely a currency code like USD, EUR, TRY
                                          finalCurrencyCode = currency;
                                        } else {
                                          // Likely a currency ID, look it up
                                          const currencyObj = currencies.find(c => c.id === currency);
                                          if (currencyObj && (currencyObj as any).code) {
                                            finalCurrencyCode = (currencyObj as any).code;
                                          }
                                        }
                                      } else if (currency && typeof currency === 'object' && (currency as any).code) {
                                        // Currency is already an object with code
                                        finalCurrencyCode = (currency as any).code;
                                      }
                                      
                                      return formatWithSymbol(balance, finalCurrencyCode);
                                    })()}
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-auto p-4 space-y-2 border-t border-white/5">
              <Link
                to="/app/settings"
                onClick={() => setIsMobileSidebarOpen(false)}
                className={cn(
                  "flex items-center px-4 py-3 text-sm font-medium text-[#439476] hover:bg-[#439476]/10 rounded-lg transition-all duration-200",
                  isDesktopSidebarCollapsed && "justify-center"
                )}
                title={isDesktopSidebarCollapsed ? t('dashboard.settings.title') : undefined}
              >
                <Settings className={cn("w-5 h-5", !isDesktopSidebarCollapsed && (i18n.language === 'ar' ? "ml-3" : "mr-3"))} />
                {!isDesktopSidebarCollapsed && t('dashboard.settings.title')}
              </Link>
              <button
                onClick={() => {
                  handleSignOut();
                  setIsMobileSidebarOpen(false);
                }}
                className="flex items-center w-full px-4 py-2.5 text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all duration-200"
              >
                <LogOut className={cn("w-5 h-5", !isDesktopSidebarCollapsed && (i18n.language === 'ar' ? "ml-3" : "mr-3"))} />
                {!isDesktopSidebarCollapsed && t('dashboard.signOut.title')}
              </button>
            </div>
          </div>
        </aside>

        <Button
          variant="ghost"
          className="lg:hidden fixed bottom-6 right-6 h-14 w-14 rounded-full bg-accent hover:bg-accent-hover text-white shadow-lg backdrop-blur-lg backdrop-saturate-150 transition-all duration-200 md:hidden z-50"
          onClick={() => navigate('/app/transactions')}
        >
          <Plus className="h-6 w-6" />
        </Button>

        <AddFolderModal
          isOpen={showAddFolder}
          onClose={() => setShowAddFolder(false)}
        />

        {editingFolder && (
          <EditFolderModal
            folder={editingFolder as Folder}
            isOpen={!!editingFolder}
            onClose={() => setEditingFolder(null)}
          />
        )}

        <EnhancedSmartListModal
          isOpen={showAddSmartList}
          onClose={() => setShowAddSmartList(false)}
        />

        {editingSmartList && (
          <EditSmartListModal
            isOpen={showEditSmartListModal}
            onClose={handleCloseEditModal}
            smartList={editingSmartList as SmartList}
          />
        )}
      </div>

      <Button
        size="icon"
        className="lg:hidden fixed bottom-6 right-6 h-14 w-14 rounded-full bg-accent hover:bg-accent-hover text-white shadow-lg backdrop-blur-lg backdrop-saturate-150 transition-all duration-200 md:hidden z-50"
        onClick={() => navigate('/app/transactions')}
      >
        <Plus className="h-6 w-6" />
      </Button>

      <AddFolderModal
        isOpen={showAddFolder}
        onClose={() => setShowAddFolder(false)}
      />

      {editingFolder && (
        <EditFolderModal
          folder={editingFolder as Folder}
          isOpen={!!editingFolder}
          onClose={() => setEditingFolder(null)}
        />
      )}

      <EnhancedSmartListModal
        isOpen={showAddSmartList}
        onClose={() => setShowAddSmartList(false)}
      />

      {editingSmartList && (
        <EditSmartListModal
          isOpen={showEditSmartListModal}
          onClose={handleCloseEditModal}
          smartList={editingSmartList as SmartList}
        />
      )}
    </>
  );
}
